import type { FC } from 'react';
import { useRef } from 'react';
import { Button } from '@components/ui/button';
import { Spinner } from '@components/ui/spinner';
import type { Virtualizer } from '@tanstack/react-virtual';
import { ArrowUpIcon } from 'lucide-react';

import type { ScrollDirection } from '../helpers/getVisibleCounterRange';
import { getVisibleCounterRange } from '../helpers/getVisibleCounterRange';

export interface TableFooterProps {
  totalResults: number;
  dataLength: number;
  isFetching: boolean;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  pageSize?: number;
}

export const TableFooter: FC<TableFooterProps> = ({
  totalResults,
  dataLength,
  isFetching,
  rowVirtualizer,
  pageSize,
}) => {
  const range = rowVirtualizer.range;
  const startIndex = range?.startIndex ?? 0;
  const endIndex = range?.endIndex ?? dataLength - 1;

  // `scrollDirection` resets to null when scrolling stops, so remember the last
  // known direction. This keeps the counter from flipping when the user halts
  // with the visible window straddling a page boundary.
  const lastDirectionRef = useRef<ScrollDirection>('forward');
  if (rowVirtualizer.scrollDirection) {
    lastDirectionRef.current = rowVirtualizer.scrollDirection;
  }

  // Derive both counters from a single page index so they always move in
  // lockstep by exactly `pageSize`, based on the leading edge of the scroll.
  const { start, end } = getVisibleCounterRange({
    startIndex,
    endIndex,
    totalResults,
    pageSize,
    direction: lastDirectionRef.current,
  });

  return (
    <footer className="bg-info-50 relative flex w-full items-center p-3">
      <div>
        <p className="text-sm leading-5 font-normal text-gray-700">
          {dataLength === 0 ? (
            <>
              Showing <strong>0</strong> of <strong>{totalResults}</strong>
            </>
          ) : (
            <>
              Showing <strong>{start}</strong> to <strong>{end}</strong> of <strong>{totalResults}</strong>
            </>
          )}
        </p>
      </div>
      <div className="grow">
        {isFetching && (
          <div className="flex items-center justify-center gap-1">
            <Spinner className="size-4" />
            <span className="text-sm leading-5 font-normal text-gray-500">Loading more records</span>
          </div>
        )}
      </div>
      <div className="flex cursor-pointer items-center">
        <Button
          className="text-blue-600"
          size="sm"
          variant="link"
          onClick={() => rowVirtualizer.scrollToIndex(0, { behavior: 'smooth' })}
        >
          Back to top
          <ArrowUpIcon />
        </Button>
      </div>
    </footer>
  );
};
